import { UploadRejectedError } from '../errors/upload-rejected-error';
import { FlowDocumentProcessingError } from '../hooks/use-flow-document-processing';
import type { UploadFlowDocumentBatchResult } from '../hooks/use-upload-flow-document-v2';

/** Shown whenever the failure is ours rather than something the user can fix. */
export const GENERIC_UPLOAD_ERROR =
  'Something went wrong uploading your document. Our team has been notified and will investigate the issue.';

/**
 * Picks the message to show for a thrown upload failure. Only
 * an UploadRejectedError or FlowDocumentProcessingError
 * carries copy written for the user; everything else (server
 * 4xx bodies, 5xx, transport) hides behind the fallback with
 * the Sentry report standing in for the detail.
 */
export function resolveUploadErrorMessage(
  error: unknown,
  fallback: string = GENERIC_UPLOAD_ERROR
): string {
  if (error instanceof UploadRejectedError && error.message) {
    return error.message;
  }
  if (error instanceof FlowDocumentProcessingError && error.message) {
    return error.message;
  }
  return fallback;
}

/**
 * Same decision for a batch. One rejection has a reason worth showing; a
 * mixed batch keeps the tally, since the reasons can differ per file.
 */
export function resolveBatchUploadErrorMessage(
  result: UploadFlowDocumentBatchResult,
  fallback: string = GENERIC_UPLOAD_ERROR
): string {
  const { failed, total } = result.summary;
  if (failed === 1) {
    const userMessage = result.items.find(
      item => item.status === 'failed'
    )?.userMessage;
    if (userMessage) return userMessage;
  }
  return total === 1
    ? fallback
    : `${failed} of ${total} documents failed to upload.`;
}
